import {
  ProductStatus,
  ProductVariantsUpdateInput,
  useProductsQuery,
  useProductUpdateMutation,
} from 'lib/graphql/storeadmin'
import {
  OrderPurpose,
  useOrdersForObserverQuery,
} from 'lib/graphql/observer/types-and-hooks'
import { Events } from 'components/bookings/calendar/types'

// カレンダー設定用カスタムフック
export const useCalendarEvents = () => {
  // オブザーバーのデータを取得
  const {
    data: ordersData,
    loading: eventsLoading,
    refetch: eventsRefetch,
  } = useOrdersForObserverQuery()

  // アクティブの施設のデータを取得
  const {
    data: productsData,
    loading: productsLoading,
    refetch: productRefetch,
  } = useProductsQuery({ variables: { status: ProductStatus.Active } })
  const [update] = useProductUpdateMutation({
    onCompleted: () => {
      productRefetch()
    },
  })

  // 利用目的に基づいてイベントの色を決定する関数
  // const getEventColor = (purpose) => {
  //   switch (purpose) {
  //     case OrderPurpose.Concert:
  //       return '#F1B924' // 黄
  //     case OrderPurpose.Sport:
  //       return '#3AB54A' // 緑
  //     case OrderPurpose.Other:
  //       return '#46C3EF' // 青
  //     default:
  //       return null // デフォルトの色
  //   }
  // }

  // 利用目的をカタカナ表記にフォーマットする関数
  const formatPurpose = (purpose) => {
    switch (purpose) {
      case OrderPurpose.Concert:
        return 'コンサート'
      case OrderPurpose.Sport:
        return 'スポーツ'
      case OrderPurpose.Other:
        return 'MIC・その他'
    }
  }

  // カレンダー表示切替の関数
  const toggleDisplay = async (productId, variantId, display: boolean) => {
    const selectedProduct = productsData?.products.edges.find(
      (product) => product.product.id === productId
    ).product
    const newVariants =
      selectedProduct?.variants.edges.length > 0
        ? selectedProduct?.variants.edges.map<ProductVariantsUpdateInput>(
            (variant) => {
              const { __typename, id, title, selectedOptions, ...rest } =
                variant.variant
              return {
                ...rest,
                inventory: 0,
                selectedOptions: variant.variant.selectedOptions.map(
                  (option) => option.value.jaJp
                ),
                periodForSale: null,
                displayCalendar:
                  variant.variant.id === variantId
                    ? display
                    : rest.displayCalendar,
              }
            }
          )
        : []

    const variables = {
      id: productId,
      input: {
        name: selectedProduct.name.jaJp,
        description: selectedProduct.description.jaJp,
        status: selectedProduct.status,
        baseTimeUnit: selectedProduct.baseTimeUnit,
        approvalRequired: selectedProduct.approvalRequired,
        options: selectedProduct?.options.map((option) => option.name.jaJp),
        contactRequiredInput: {
          addedList: [],
          deletedList: [],
        },
        variants: newVariants,
      },
    }

    try {
      await update({
        variables: variables,
      })
    } catch (error) {
      console.error(error)
      return
    }
  }

  // カレンダー背景色切替の関数
  const changeBgColor = async (
    productId: string,
    variantId: string,
    color: string
  ) => {
    const selectedProduct = productsData?.products.edges.find(
      (product) => product.product.id === productId
    ).product
    const newVariants =
      selectedProduct?.variants.edges.length > 0
        ? selectedProduct?.variants.edges.map<ProductVariantsUpdateInput>(
            (variant) => {
              const { __typename, id, title, selectedOptions, ...rest } =
                variant.variant
              return {
                ...rest,
                inventory: 0,
                selectedOptions: variant.variant.selectedOptions.map(
                  (option) => option.value.jaJp
                ),
                periodForSale: null,
                colorCode:
                  variant.variant.id === variantId ? color : rest.colorCode,
              }
            }
          )
        : []

    const variables = {
      id: productId,
      input: {
        name: selectedProduct.name.jaJp,
        description: selectedProduct.description.jaJp,
        status: selectedProduct.status,
        baseTimeUnit: selectedProduct.baseTimeUnit,
        approvalRequired: selectedProduct.approvalRequired,
        options: selectedProduct?.options.map((option) => option.name.jaJp),
        contactRequiredInput: {
          addedList: [],
          deletedList: [],
        },
        variants: newVariants,
      },
    }

    try {
      await update({
        variables: variables,
      })
    } catch (error) {
      console.error(error)
    }
  }

  // カレンダー用の配列
  const events: Events[] = ordersData?.orders.edges.flatMap((order) => {
    const orderNode = order.node
    const purpose = formatPurpose(orderNode.purpose)
    const lineItems = orderNode.lineItems.edges

    // バリエーション単位での成形
    // ・displayCalendarがtrueの予約だけ返す
    const calendarEvents = lineItems
      .flatMap((item) => {
        if (item.node.displayCalendar === false) {
          return null
        }

        return item.node.arenaBookingInfo.map((date) => ({
          title: purpose,
          productName: item.node.name.jaJp,
          purpose: item.node.variantTitle.jaJp,
          start: `${date.dateOfUse}T${orderNode.period.start}`,
          end: `${date.dateOfUse}T${orderNode.period.end}`,
          numberOfPeople: `${orderNode.numberOfPeople}`,
          color: item.node.colorCode,
        }))
      })
      .filter(Boolean)

    return calendarEvents
  })

  return {
    events,
    eventsLoading,
    eventsRefetch,
    productsData,
    productsLoading,
    productRefetch,
    toggleDisplay,
    changeBgColor,
  }
}
